import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Volume2, VolumeX, LogOut, Package, Layers, Wrench, Clock, Camera, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { APP_VERSION } from '../../version';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PerfilScreen({ userId, username, onLogout, onFotoUpdated }) {
  const [operador, setOperador] = useState(null);
  const [stats, setStats] = useState({ ofs: 0, lotes: 0, operacoes: 0, tempo_minutos: 0 });
  const [loading, setLoading] = useState(true);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const [somLigado, setSomLigado] = useState(() => {
    try { return localStorage.getItem('som_ligado') !== 'false'; } catch { return true; }
  });
  const [filtroTempo, setFiltroTempo] = useState('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch operador info
      let opData = null;
      if (userId) {
        try {
          const res = await axios.get(`${API}/operadores/${userId}`);
          opData = res.data;
        } catch { /* silent */ }
      }
      if (!opData && username) {
        try {
          const res = await axios.get(`${API}/operadores`);
          const ops = res.data || [];
          opData = ops.find(o => o.username === username || o.id === userId);
        } catch { /* silent */ }
      }
      setOperador(opData);

      // Fetch stats from registos-tempo
      let params = {};
      const now = new Date();
      if (filtroTempo === 'hoje') {
        params.data_inicio = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        params.data_fim = now.toISOString();
      } else if (filtroTempo === 'semana') {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        params.data_inicio = new Date(now.getFullYear(), now.getMonth(), diff).toISOString();
        params.data_fim = now.toISOString();
      } else if (filtroTempo === 'data' && dataInicio && dataFim) {
        params.data_inicio = new Date(dataInicio).toISOString();
        params.data_fim = new Date(dataFim + 'T23:59:59').toISOString();
      }

      const operadorId = opData?.id || userId;
      if (operadorId) {
        try {
          const res = await axios.get(`${API}/registos`, { params: { operador_id: operadorId, limit: 5000 } });
          let registos = res.data || [];
          
          // Filtrar por data
          if (params.data_inicio) {
            const inicio = new Date(params.data_inicio);
            const fim = params.data_fim ? new Date(params.data_fim) : new Date();
            registos = registos.filter(r => {
              const d = new Date(r.inicio || r.criado_em);
              return d >= inicio && d <= fim;
            });
          }
          
          const ofsSet = new Set();
          const lotesSet = new Set();
          const opsSet = new Set();
          let totalMinutos = 0;
          registos.forEach(r => {
            if (r.of_id) ofsSet.add(r.of_id);
            if (r.lote_id) lotesSet.add(r.lote_id);
            if (r.operacao_id) opsSet.add(r.operacao_id);
            if (r.duracao_minutos) totalMinutos += r.duracao_minutos;
            else if (r.inicio && r.fim) {
              totalMinutos += (new Date(r.fim) - new Date(r.inicio)) / 60000;
            }
          });
          setStats({ ofs: ofsSet.size, lotes: lotesSet.size, operacoes: opsSet.size, tempo_minutos: Math.round(totalMinutos) });
        } catch {
          setStats({ ofs: 0, lotes: 0, operacoes: 0, tempo_minutos: 0 });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [userId, username, filtroTempo, dataInicio, dataFim]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSom = () => {
    const novo = !somLigado;
    setSomLigado(novo);
    try { localStorage.setItem('som_ligado', String(novo)); } catch { /* silent */ }
  };

  const formatTempo = (min) => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  const nomeOperador = operador?.nome_completo || operador?.nome || username || 'Operador';
  const fotoUrl = operador?.foto_url
    ? (operador.foto_url.startsWith('http') ? operador.foto_url : `${BACKEND_URL}${operador.foto_url}`)
    : null;

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ficheiro muito grande. Maximo 10MB.');
      return;
    }
    // Ler o ficheiro e abrir o crop dialog
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCropDialogOpen(true);
      setCrop({ unit: '%', width: 80, height: 80, x: 10, y: 10 });
    };
    reader.readAsDataURL(file);
    // Reset file input para permitir re-selecionar o mesmo ficheiro
    e.target.value = '';
  };

  const getCroppedBlob = () => {
    return new Promise((resolve) => {
      const image = imgRef.current;
      if (!image || !completedCrop) { resolve(null); return; }
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const size = 400; // Output 400x400
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX, completedCrop.y * scaleY,
        completedCrop.width * scaleX, completedCrop.height * scaleY,
        0, 0, size, size
      );
      canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });
  };

  const handleCropConfirm = async () => {
    const opId = operador?.id || userId;
    if (!opId) return;
    setUploadingFoto(true);
    try {
      const blob = await getCroppedBlob();
      if (!blob) { toast.error('Erro ao recortar'); return; }
      const formData = new FormData();
      formData.append('file', blob, 'avatar.jpg');
      const response = await axios.post(`${API}/operadores/${opId}/foto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto atualizada!');
      setCropDialogOpen(false);
      setImageSrc(null);
      fetchData();
      // Notify parent to update avatar in header
      const novaUrl = response.data?.foto_url || response.data?.url;
      if (novaUrl && onFotoUpdated) {
        onFotoUpdated(novaUrl);
      }
    } catch {
      toast.error('Erro ao carregar foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  return (
    <div className="flex flex-col h-screen" style={{ background: '#F8FAFC' }}>
      {/* Conteúdo scrollável */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-24" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Header com nome e foto */}
      <div className="px-5 py-6" style={{ background: '#FFFFFF' }}>
        <div className="flex items-center gap-4">
          <div className="relative">
            {fotoUrl ? (
              <img
                src={fotoUrl}
                alt={nomeOperador}
                className="w-16 h-16 rounded-full object-cover border-2"
                style={{ borderColor: '#0B3B30' }}
                data-testid="perfil-foto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-black text-2xl flex-shrink-0"
                style={{ background: '#0B3B30' }}
                data-testid="perfil-avatar-letra"
              >
                {nomeOperador.charAt(0).toUpperCase()}
              </div>
            )}
            {/* Botao de camera para trocar foto */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFoto}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-md border-2 border-white"
              style={{ background: '#0B3B30' }}
              data-testid="btn-trocar-foto"
            >
              {uploadingFoto ? (
                <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Camera className="h-3.5 w-3.5 text-white" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFotoUpload}
              className="hidden"
              data-testid="input-foto-perfil"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-black text-xl truncate" style={{ color: '#1E293B' }} data-testid="perfil-nome">
              {nomeOperador}
            </h1>
            {username && (
              <p className="text-sm" style={{ color: '#94A3B8' }}>@{username}</p>
            )}
          </div>
        </div>
      </div>

      {/* Som toggle */}
      <div className="mx-5 mt-4">
        <div
          className="flex items-center justify-between p-4 rounded-2xl"
          style={{ background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        >
          <div className="flex items-center gap-3">
            {somLigado
              ? <Volume2 className="h-5 w-5" style={{ color: '#10B981' }} />
              : <VolumeX className="h-5 w-5" style={{ color: '#94A3B8' }} />}
            <span className="font-semibold text-sm" style={{ color: '#1E293B' }}>
              Som de notificações
            </span>
          </div>
          <button
            onClick={toggleSom}
            className="relative w-12 h-7 rounded-full transition-colors"
            style={{ background: somLigado ? '#10B981' : '#CBD5E1' }}
            data-testid="toggle-som"
          >
            <div
              className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
              style={{ left: somLigado ? '22px' : '2px' }}
            />
          </button>
        </div>
      </div>

      {/* Filtro temporal */}
      <div className="mx-5 mt-4">
        <div className="flex gap-2">
          {[
            { id: 'hoje', label: 'Hoje' },
            { id: 'semana', label: 'Esta Semana' },
            { id: 'data', label: 'Data Definida' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFiltroTempo(f.id)}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all"
              style={{
                background: filtroTempo === f.id ? '#0B3B30' : '#FFFFFF',
                color: filtroTempo === f.id ? '#FFFFFF' : '#64748B',
                boxShadow: filtroTempo === f.id ? 'none' : '0 2px 8px rgba(0,0,0,0.05)',
              }}
              data-testid={`filtro-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {filtroTempo === 'data' && (
          <div className="flex gap-2 mt-2">
            <input
              type="date"
              value={dataInicio}
              onChange={e => setDataInicio(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border text-sm"
              style={{ borderColor: '#E2E8F0' }}
              data-testid="data-inicio"
            />
            <input
              type="date"
              value={dataFim}
              onChange={e => setDataFim(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border text-sm"
              style={{ borderColor: '#E2E8F0' }}
              data-testid="data-fim"
            />
          </div>
        )}
      </div>

      {/* Estatísticas */}
      <div className="mx-5 mt-4 grid grid-cols-2 gap-3">
        {loading ? (
          <div className="col-span-2 flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: '#0B3B30', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <>
            <StatCard icon={Package} label="OFs Trabalhadas" value={stats.ofs} color="#3B82F6" />
            <StatCard icon={Layers} label="Lotes" value={stats.lotes} color="#8B5CF6" />
            <StatCard icon={Wrench} label="Operações" value={stats.operacoes} color="#F59E0B" />
            <StatCard icon={Clock} label="Tempo Total" value={formatTempo(stats.tempo_minutos)} color="#10B981" />
          </>
        )}
      </div>

      {/* Botão Sair */}
      <div className="mx-5 mt-6">
        <button
          onClick={() => onLogout && onLogout()}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-base transition-all hover:opacity-90"
          style={{ background: '#FEE2E2', color: '#DC2626' }}
          data-testid="btn-sair"
        >
          <LogOut className="h-5 w-5" />
          Sair
        </button>
      </div>

      {/* Versao da app */}
      <div className="text-center mt-4 mb-2">
        <span className="text-xs px-3 py-1 rounded-full bg-stone-100 text-stone-400" data-testid="app-version">
          {APP_VERSION}
        </span>
      </div>
      </div>{/* fim conteúdo scrollável */}

      {/* Crop Dialog Overlay */}
      {cropDialogOpen && imageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="w-full max-w-md mx-4 bg-white rounded-3xl overflow-hidden shadow-2xl">
            <div className="px-5 py-3 flex items-center justify-between border-b" style={{ background: '#0B3B30' }}>
              <h3 className="text-white font-bold text-base">Ajustar foto</h3>
              <button onClick={() => { setCropDialogOpen(false); setImageSrc(null); }} className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center" style={{ maxHeight: '55vh', overflow: 'auto' }}>
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Ajustar"
                  style={{ maxHeight: '50vh', maxWidth: '100%' }}
                  data-testid="crop-image-preview"
                />
              </ReactCrop>
            </div>
            <div className="px-5 py-3 flex gap-3 border-t">
              <button
                onClick={() => { setCropDialogOpen(false); setImageSrc(null); }}
                className="flex-1 py-3 rounded-2xl font-bold text-sm border-2 bg-white"
                style={{ color: '#64748B', borderColor: '#E2E8F0' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={uploadingFoto}
                className="flex-1 py-3 rounded-2xl font-bold text-sm text-white flex items-center justify-center gap-1.5"
                style={{ background: uploadingFoto ? '#94A3B8' : '#0B3B30' }}
                data-testid="btn-confirmar-crop"
              >
                {uploadingFoto ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <><Check className="h-4 w-4" /> Guardar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div
      className="p-4 rounded-2xl flex flex-col gap-2"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-black" style={{ color: '#1E293B' }}>{value}</p>
        <p className="text-xs font-medium" style={{ color: '#94A3B8' }}>{label}</p>
      </div>
    </div>
  );
}
